import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useInView } from "framer-motion";
import { ArrowUpRight, Maximize2 } from "lucide-react";
import { FadeUp, EASE } from "./Reveal";
import { Lightbox } from "./Lightbox";

const ASSETS = "https://customer-assets-agu9un31.emergentagent.net/job_future-portfolio-34/artifacts";
const CYCLE_MS = 3500;

const PROJECTS = [
  {
    id: "01",
    title: "Patterns",
    desc: "Hand-made pattern systems — a grunge zebra print with neon-pink stars, a forest-green halftone lace, a grey crosshair collage, a yellow Keep Out tape field and a warped red checkerboard. Rhythm, repetition and texture built for print and product.",
    tags: ["Pattern", "Texture", "Print"],
    type: "Patterns",
    img: "/patterns/zebra.webp",
    images: ["/patterns/zebra.webp", "/patterns/lace.webp", "/patterns/crosshair.webp?v=2", "/patterns/keepout.webp?v=2", "/patterns/checker.webp"],
    span: "md:col-span-7",
    ratio: "aspect-[16/11] md:aspect-auto md:flex-1 md:min-h-0",
    fill: true,
  },
  {
    id: "02",
    title: "Posters & Products",
    desc: "Poster and product design — the Multi-Focused series, Observe, Human, Jacob's Protein Soda, Northern Farms Honey BBQ and the BT tee, all built in Adobe.",
    tags: ["Posters", "Products", "Print"],
    type: "Posters & Products",
    img: `${ASSETS}/ee388cb8_multi%20focused.webp`,
    images: [
      `${ASSETS}/ee388cb8_multi%20focused.webp`,
      `${ASSETS}/4osdkn6b_multi%20focus.jpg`,
      "/posters/observe.webp",
      "/posters/human.webp",
      `${ASSETS}/3yladn0n_POSTER%202-01.webp`,
      `${ASSETS}/88q5s3o2_chip%20bag.webp`,
      `${ASSETS}/5niic9ki_shirt%20mockup.webp`,
    ],
    span: "md:col-span-5",
    ratio: "aspect-[4/5]",
    contain: true,
  },
  {
    id: "03",
    title: "Tri-City Athletic Club",
    desc: "Website design for a gym established in 1966 — old-school equipment, serious training.",
    tags: ["Website", "Responsive", "Brand"],
    type: "Web",
    img: "/tcac.jpg?v=2",
    link: "https://www.tri-cityathleticclub.com/",
    span: "md:col-span-12",
    ratio: "aspect-[16/7] md:aspect-[21/6]",
  },
];

function ProjectCard({ p, index }) {
  const [active, setActive] = useState(0);
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.4 });
  const images = p.images || [p.img];
  const src = images[active];
  const count = images.length;
  const cycling = count > 1 && !hovered && !open && inView;

  useEffect(() => {
    if (!cycling) return undefined;
    const id = setInterval(() => setActive((i) => (i + 1) % count), CYCLE_MS);
    return () => clearInterval(id);
  }, [cycling, count]);

  return (
    <FadeUp
      delay={(index % 2) * 0.1}
      className={`${p.span} col-span-1`}
      data-testid={`project-card-${p.id}`}
    >
      <motion.article
        ref={ref}
        whileHover="hover"
        onHoverStart={() => setHovered(true)}
        onHoverEnd={() => setHovered(false)}
        data-cycling={cycling ? "true" : "false"}
        className="card-lift group flex h-full flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#0a0a0a]"
        data-testid={`project-article-${p.id}`}
      >
        <div className={`relative overflow-hidden bg-[#0d0d0d] ${p.ratio}`}>
          {p.contain && (
            <img
              src={src}
              alt=""
              aria-hidden
              className="absolute inset-0 h-full w-full scale-110 object-cover opacity-30 blur-xl"
              style={{ willChange: "transform" }}
            />
          )}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 1, ease: EASE }}
            className={`h-full w-full ${p.fill ? "absolute inset-0" : "relative"} ${p.contain ? "px-5 pt-5 pb-20 md:px-6 md:pt-6" : ""}`}
          >
            <button
              type="button"
              onClick={() => setOpen(true)}
              aria-label={`Open ${p.title} gallery`}
              className="group/img block h-full w-full cursor-zoom-in"
              data-testid={`project-open-${p.id}`}
            >
              <AnimatePresence mode="popLayout" initial={false}>
                <motion.img
                  key={src}
                  src={src}
                  alt={`${p.title} project`}
                  initial={{ opacity: 0, scale: 1.04 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.6, ease: EASE }}
                  className={`h-full w-full ${p.contain ? "object-contain drop-shadow-[0_20px_50px_rgba(0,0,0,0.6)]" : "object-cover"}`}
                  data-testid={`project-image-${p.id}`}
                />
              </AnimatePresence>
            </button>
          </motion.div>
          <div className="pointer-events-none absolute right-4 top-4 flex items-center gap-2 rounded-full border border-white/15 bg-black/75 px-3 py-1.5 font-jbmono text-[10px] tracking-[0.25em] text-zinc-200 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
            <Maximize2 size={12} /> EXPAND
          </div>
          {images.length > 1 && (
            <div
              className="absolute bottom-4 left-4 flex max-w-[calc(100%-2rem)] flex-wrap gap-1.5 rounded-full border border-white/15 bg-black/75 p-1.5 pb-3"
              data-testid={`project-gallery-${p.id}`}
            >
              {images.map((im, i) => (
                <div key={im} className="relative">
                  <button
                    type="button"
                    aria-label={`View image ${i + 1}`}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => setActive(i)}
                    className={`h-9 w-9 overflow-hidden rounded-full border transition-[border-color,transform] duration-300 hover:scale-110 ${
                      i === active ? "border-[#00E5FF]" : "border-white/20 opacity-70"
                    }`}
                    data-testid={`project-thumb-${p.id}-${i}`}
                  >
                    <img src={im} alt="" className="h-full w-full object-cover" />
                  </button>
                  {i === active && (
                    <span className="absolute -bottom-2 left-1 right-1 h-[2px] overflow-hidden rounded-full bg-white/15">
                      <motion.span
                        key={`${active}-${cycling}`}
                        initial={{ scaleX: 0 }}
                        animate={{ scaleX: cycling ? 1 : 0 }}
                        transition={{ duration: cycling ? CYCLE_MS / 1000 : 0.2, ease: "linear" }}
                        className="block h-full w-full origin-left bg-[#00E5FF]"
                        data-testid={`project-progress-${p.id}`}
                      />
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
          <div className="absolute left-4 top-4 rounded-full border border-white/15 bg-black/75 px-3 py-1 font-jbmono text-[10px] tracking-[0.25em] text-zinc-300">
            {p.type}
          </div>
        </div>
        <div className="flex items-start justify-between gap-4 p-6 md:p-7">
          <div>
            <div className="mb-2 flex items-baseline gap-3">
              <span className="font-jbmono text-xs text-[#00E5FF]">{p.id}</span>
              <h3 className="font-display text-2xl font-bold tracking-tight md:text-3xl" data-testid={`project-title-${p.id}`}>
                {p.title}
              </h3>
            </div>
            <p className="max-w-md text-sm leading-relaxed text-zinc-400 md:text-base">{p.desc}</p>
            {p.link && (
              <a
                href={p.link}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-block font-jbmono text-xs tracking-[0.15em] text-[#00E5FF] underline-offset-4 hover:underline"
                data-testid={`project-link-${p.id}`}
              >
                {p.link.replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "")}
              </a>
            )}
            <div className="mt-4 flex flex-wrap gap-2">
              {p.tags.map((t) => (
                <span
                  key={t}
                  className="rounded-full border border-white/10 px-3 py-1 font-jbmono text-[10px] uppercase tracking-[0.15em] text-zinc-400"
                >
                  {t}
                </span>
              ))}
            </div>
          </div>
          <motion.a
            href={p.link || "#work"}
            target={p.link ? "_blank" : undefined}
            rel={p.link ? "noopener noreferrer" : undefined}
            aria-label={p.link ? `Visit ${p.title}` : p.title}
            variants={{ hover: { backgroundColor: "#00E5FF", color: "#050505", rotate: 45 } }}
            transition={{ duration: 0.4, ease: EASE }}
            className="shrink-0 rounded-full border border-white/15 p-3 text-white"
            data-testid={`project-arrow-${p.id}`}
          >
            <ArrowUpRight size={18} />
          </motion.a>
        </div>
      </motion.article>
      <Lightbox
        open={open}
        images={images}
        index={active}
        title={p.title}
        onClose={() => setOpen(false)}
        onChange={setActive}
      />
    </FadeUp>
  );
}

export default function Work() {
  return (
    <section id="work" data-testid="work-section" className="relative mx-auto max-w-[1400px] px-6 py-16 md:px-10 md:py-24">
      <FadeUp>
        <div className="mb-10 flex items-end justify-between md:mb-14">
          <div>
            <p className="mb-4 font-jbmono text-xs uppercase tracking-[0.3em] text-[#00E5FF]">
              Selected work
            </p>
            <h2 className="font-display text-4xl font-black uppercase tracking-tight md:text-6xl">
              Things I&apos;ve built
            </h2>
          </div>
          <span className="hidden font-jbmono text-sm text-zinc-500 md:block">(03)</span>
        </div>
      </FadeUp>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-12 md:gap-8">
        {PROJECTS.map((p, i) => (
          <ProjectCard key={p.id} p={p} index={i} />
        ))}
      </div>
    </section>
  );
}
